import { Box } from "@mui/material";
import { type UseModalState } from "@src/lib/ui-react";

import { Divider } from "../components/Divider";
import { ChatHeader } from "./ChatHeader";
import { ChatInput } from "./ChatInput";
import { ChatPanelMessageContent } from "./ChatPanelMessageContent";
import { ChatAdminReadOnly } from "./components/ChatAdminReadOnly";
import { ChatReplyPreview } from "./components/ChatReplyPreview";
import { useChatPanelState } from "./hooks/useChatPanelState";

export interface ChatPanelProps {
  modalState: UseModalState;
  /**
   * Workplace ID for the chat channel. When omitted, defaults to the
   * logged-in user's profile ID from the session.
   */
  workplaceId?: string;
  /**
   * Worker ID for API integration to fetch/send chat messages
   */
  workerId: string;
  workerName: string;
  /**
   * When provided, loads the placement-specific channel (3-part URL).
   */
  placementId?: string;
  /**
   * Optional callback when chat panel is closed
   */
  onClose?: () => void;
}

/**
 * Chat panel component for worker communication.
 * Displays chat messages with a worker and provides an input to send new messages.
 * Connected to Sendbird for real-time messaging.
 */
export function ChatPanel(props: ChatPanelProps) {
  const {
    modalState,
    workplaceId: workplaceIdFromCaller,
    workerId,
    workerName,
    placementId,
    onClose,
  } = props;

  const {
    workplaceId,
    isAdmin,
    isLoading,
    isError,
    isBlocked,
    isSendingDisabled,
    messages,
    hasMore,
    isLoadingMore,
    exclusion,
    replyTo,
    messagesEndRef,
    handleClose,
    handleSend,
    handleFileSelect,
    handleRetry,
    loadMore,
    setReplyTo,
    clearReply,
  } = useChatPanelState({
    modalState,
    workplaceId: workplaceIdFromCaller,
    workerId,
    placementId,
    onClose,
  });

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
      })}
    >
      <Box sx={{ padding: 5 }}>
        <ChatHeader workerId={workerId} workerName={workerName} onClose={handleClose} />
      </Box>

      <Divider />

      <Box
        role="log"
        aria-label="Chat messages"
        aria-live="polite"
        sx={{ flex: 1, overflowY: "auto", padding: 5 }}
      >
        <ChatPanelMessageContent
          messages={messages}
          workerName={workerName}
          workplaceId={workplaceId}
          isLoading={isLoading}
          isError={isError}
          isBlocked={isBlocked}
          isAdmin={isAdmin}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          exclusion={exclusion}
          messagesEndRef={messagesEndRef}
          onRetry={handleRetry}
          onLoadMore={loadMore}
          onReply={setReplyTo}
        />
      </Box>

      {replyTo && !isAdmin && !isBlocked && (
        <ChatReplyPreview
          message={replyTo.message}
          senderName={replyTo.senderName}
          onCancel={clearReply}
        />
      )}

      {!isBlocked && (
        <Box sx={{ padding: 5 }}>
          {isAdmin ? (
            <ChatAdminReadOnly />
          ) : (
            <ChatInput
              disabled={isSendingDisabled}
              onSend={handleSend}
              onFileSelect={handleFileSelect}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
